import { Fragment, useMemo, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
} from '@/components/ui/combobox';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  sanitizeProximityKm,
  type AttributeCondition,
  type OrgEventAutomationCondition,
  type OrgEventAutomationSource,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { useAttributeAutomationRuleConditionsFlag } from 'utils/featureFlags';
import { useAorText } from 'utils/aorTerminology';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import { ArcGisLayerPicker, type PickedArcGisLayer } from './ArcGisLayerPicker';
import { findWeatherLayerByUrl } from './weatherFeatureServerCatalog';
import {
  automationReadinessLabel,
  buildArcGisWhere,
  operatorLabel,
  operatorsForFieldWithSemantics,
  snapshotField,
  type ArcGisConditionOperator,
  type ArcGisFieldSchema,
} from './arcgisLayerSchema';
import { findSchemaFixture, getMockMatchCount } from './arcgisLayerSchemaMocks';
import { resolveFieldSemantics, semanticsForPreset } from './fieldSemantics';
import {
  previewArcGisMatchCount,
  useArcGisLayerSchema,
} from './useArcGisLayerSchema';
import { feedPresetFromSource, sourceForPreset } from './automationRuleLogic';
import {
  DEFAULT_WITHIN_DISTANCE_KM,
  FEED_OPTIONS,
  applyCriterionKind,
  applyPresetToCondition,
  criterionTypeFromCondition,
  visibleCriterionOptions,
  type CriterionKind,
  type SelectOption,
  type SourcePreset,
} from './ruleFormModel';
import {
  CriterionHelpIcon,
  SimpleSelect,
  WeatherLayerUrlField,
} from './automationFormControls';

function DistanceKmField({
  proximityKm,
  onChange,
}: {
  proximityKm?: number;
  onChange: (proximityKm: number) => void;
}) {
  const km =
    typeof proximityKm === 'number' && Number.isFinite(proximityKm)
      ? proximityKm
      : DEFAULT_WITHIN_DISTANCE_KM;
  return (
    <Field>
      <FieldLabel>Distance (Kilometers)</FieldLabel>
      <Input
        type="number"
        step={0.1}
        value={km}
        onChange={(e) => {
          const raw = Number(e.target.value);
          if (!Number.isFinite(raw)) return;
          onChange(raw);
        }}
      />
    </Field>
  );
}

export function ConditionRow({
  condition,
  canRemove,
  onChange,
  onRemove,
  aorOptions,
}: {
  condition: OrgEventAutomationCondition;
  canRemove: boolean;
  onChange: (next: OrgEventAutomationCondition) => void;
  onRemove: () => void;
  aorOptions: SelectOption[];
}) {
  const attributeConditionsEnabled = useAttributeAutomationRuleConditionsFlag();
  const aorText = useAorText();
  const criterionOptions = visibleCriterionOptions(attributeConditionsEnabled);
  const criterionValue = criterionTypeFromCondition(condition);
  const criterionVisible = criterionOptions.some(
    (option) => option.value === criterionValue
  );
  const preset = feedPresetFromSource(condition.source);
  const isCustom = preset === 'custom';
  const isSensor = preset === 'sensor_alpha';
  const isArcGisLayer = preset === 'arcgis_layer';
  const isTropical = preset === 'tropical';
  const isWildfire = preset === 'wildfire_incidents';
  const supportsSchemaFields =
    isArcGisLayer || isCustom || isTropical || isWildfire;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const serviceUrl = condition.source?.serviceUrl?.trim() || null;
  const schemaQuery = useArcGisLayerSchema({
    serviceUrl: supportsSchemaFields ? serviceUrl : null,
    authType: condition.source?.authType,
  });

  const schemaFields = useMemo(() => {
    if (schemaQuery.data?.fields?.length) return schemaQuery.data.fields;
    // Fall back to snapshot so a saved rule still renders without a live session.
    const snap = condition.source?.fieldSnapshot;
    if (snap?.name) {
      return [
        {
          name: snap.name,
          alias: snap.alias || snap.name,
          type: (snap.type as ArcGisFieldSchema['type']) || 'string',
          codedValues: snap.codedValues,
        } satisfies ArcGisFieldSchema,
      ];
    }
    return [] as ArcGisFieldSchema[];
  }, [schemaQuery.data, condition.source?.fieldSnapshot]);

  const selectedField = useMemo(() => {
    if (condition.kind !== 'attribute' || !condition.field) return null;
    return (
      schemaFields.find((f) => f.name === condition.field) ??
      (condition.source?.fieldSnapshot?.name === condition.field
        ? ({
            name: condition.source.fieldSnapshot.name,
            alias: condition.source.fieldSnapshot.alias,
            type:
              (condition.source.fieldSnapshot
                .type as ArcGisFieldSchema['type']) || 'string',
            codedValues: condition.source.fieldSnapshot.codedValues,
          } satisfies ArcGisFieldSchema)
        : null)
    );
  }, [condition, schemaFields]);

  const selectedSemantics = useMemo(
    () =>
      condition.kind === 'attribute'
        ? resolveFieldSemantics(preset, condition.field)
        : undefined,
    [preset, condition]
  );

  const operatorOptions = useMemo(() => {
    if (supportsSchemaFields) {
      return operatorsForFieldWithSemantics(
        selectedField,
        selectedSemantics
      ).map((op) => ({
        value: op,
        label: operatorLabel(op),
      }));
    }
    return [
      { label: '≥', value: '>=' },
      { label: '>', value: '>' },
      { label: '≤', value: '<=' },
      { label: '<', value: '<' },
      { label: '=', value: '==' },
    ];
  }, [supportsSchemaFields, selectedField, selectedSemantics]);

  const fieldGroups = useMemo(() => {
    const presetSemantics = semanticsForPreset(preset);
    const recommended: ArcGisFieldSchema[] = [];
    const rest: ArcGisFieldSchema[] = [];
    for (const field of schemaFields) {
      const sem =
        presetSemantics[field.name] ??
        resolveFieldSemantics(preset, field.name);
      if (sem?.evaluatorAware) recommended.push(field);
      else rest.push(field);
    }
    return { recommended, rest, hasRecommended: recommended.length > 0 };
  }, [schemaFields, preset]);

  const fieldItems = useMemo(() => {
    if (fieldGroups.hasRecommended) {
      return [
        {
          label: 'Recommended',
          items: fieldGroups.recommended.map((f) => f.name),
        },
        {
          label: 'All fields',
          items: fieldGroups.rest.map((f) => f.name),
        },
      ].filter((g) => g.items.length > 0);
    }
    return schemaFields.map((f) => f.name);
  }, [fieldGroups, schemaFields]);

  const fieldLabelByName = useMemo(
    () => new Map(schemaFields.map((f) => [f.name, f.alias || f.name])),
    [schemaFields]
  );

  const applyPickedLayer = (picked: PickedArcGisLayer) => {
    const nextSource: OrgEventAutomationSource = {
      ...(condition.source ?? sourceForPreset('arcgis_layer')),
      type: 'arcgis_feature',
      preset: 'arcgis_layer',
      serviceUrl: picked.serviceUrl,
      layerLabel: picked.layerLabel,
      authType: picked.authType,
      externalLayerId: picked.externalLayerId,
      sublayerId: picked.sublayerId,
      fieldSnapshot: undefined,
    };
    if (condition.kind === 'attribute') {
      onChange({
        ...condition,
        field: '',
        value: '',
        operator: '==',
        source: nextSource,
      });
    } else {
      onChange({ ...condition, source: nextSource });
    }
    setPreviewCount(null);
    setPreviewError(null);
  };

  const updateAttribute = (patch: Partial<AttributeCondition>) => {
    if (condition.kind !== 'attribute') return;
    onChange({ ...condition, ...patch });
  };

  const selectField = (fieldName: string) => {
    if (condition.kind !== 'attribute') return;
    const field = schemaFields.find((f) => f.name === fieldName);
    const semantics = resolveFieldSemantics(preset, fieldName);
    const ops = operatorsForFieldWithSemantics(field, semantics);
    const nextOp = (
      ops.includes(condition.operator as ArcGisConditionOperator)
        ? condition.operator
        : ops[0] || '=='
    ) as AttributeCondition['operator'];
    onChange({
      ...condition,
      field: fieldName,
      operator: nextOp,
      value: '',
      source: {
        ...(condition.source ?? sourceForPreset(preset)),
        fieldSnapshot: field ? snapshotField(field) : undefined,
      },
    });
    setPreviewCount(null);
  };

  const runPreview = async () => {
    if (condition.kind !== 'attribute' || !serviceUrl || !condition.field) {
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const fixture = findSchemaFixture(serviceUrl);
      const where = buildArcGisWhere(
        condition.field,
        condition.operator as ArcGisConditionOperator,
        condition.value,
        selectedSemantics
      );
      const count = await previewArcGisMatchCount({
        serviceUrl,
        where,
        mockOnly: fixture?.mockOnly,
        mockCount: getMockMatchCount(serviceUrl),
      });
      setPreviewCount(count);
    } catch (err) {
      setPreviewCount(null);
      setPreviewError(
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Preview failed'
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const readiness = automationReadinessLabel(condition.source?.authType);
  const schemaError = schemaQuery.error;
  const dateFieldDisabled = selectedField?.type === 'date';

  return (
    <div className="tw:space-y-2 tw:rounded-md tw:border tw:border-border tw:bg-card/40 tw:p-3">
      <div className="tw:flex tw:flex-wrap tw:items-start tw:gap-2">
        <Field className="tw:min-w-[180px] tw:flex-1">
          <FieldLabel>Data feed</FieldLabel>
          <SimpleSelect
            value={preset}
            onChange={(next) =>
              onChange(applyPresetToCondition(condition, next as SourcePreset))
            }
            options={FEED_OPTIONS}
            placeholder={condition.source?.layerLabel || 'Select a data feed'}
          />
        </Field>
        <Field className="tw:min-w-[160px] tw:flex-1">
          <FieldLabel className="tw:flex tw:items-center tw:gap-1">
            Condition type
            <CriterionHelpIcon includeAttribute={attributeConditionsEnabled} />
          </FieldLabel>
          <SimpleSelect
            value={criterionVisible ? criterionValue : ''}
            onChange={(next) =>
              onChange(applyCriterionKind(condition, next as CriterionKind))
            }
            options={criterionOptions}
            placeholder={
              criterionVisible ? undefined : 'Select a condition type'
            }
          />
        </Field>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="tw:mt-6 tw:shrink-0 tw:px-2"
          disabled={!canRemove}
          onClick={onRemove}
          aria-label="Remove condition"
        >
          <Trash2 className="tw:h-4 tw:w-4" />
        </Button>
      </div>

      {isArcGisLayer ? (
        <div className="tw:space-y-2">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setPickerOpen(true)}
            >
              {serviceUrl ? 'Change layer' : 'Choose layer'}
            </Button>
            {condition.source?.layerLabel ? (
              <span className="tw:text-sm tw:font-medium">
                {condition.source.layerLabel}
              </span>
            ) : null}
            {serviceUrl ? (
              <Badge
                variant="outline"
                className={
                  readiness.runsInBackground
                    ? 'tw:border-emerald-500/40 tw:text-emerald-300'
                    : 'tw:border-amber-500/40 tw:text-amber-300'
                }
              >
                {readiness.label}
              </Badge>
            ) : null}
          </div>
          {serviceUrl ? (
            <p className="tw:m-0 tw:truncate tw:text-[11px] tw:text-muted-foreground">
              {serviceUrl}
            </p>
          ) : (
            <FieldDescription>
              Pick a demo, saved, portal, or URL layer to load its fields.
            </FieldDescription>
          )}
          {!readiness.runsInBackground ? (
            <p className="tw:m-0 tw:text-xs tw:text-amber-300">
              This layer will not run in the background scheduler without org
              key access.
            </p>
          ) : null}
          <ArcGisLayerPicker
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            onPick={applyPickedLayer}
            initialUrl={serviceUrl ?? undefined}
          />
        </div>
      ) : null}

      {isCustom ? (
        <WeatherLayerUrlField
          value={condition.source?.serviceUrl ?? ''}
          onChange={(serviceUrl) =>
            onChange({
              ...condition,
              source: {
                ...(condition.source ?? sourceForPreset('custom')),
                type: 'arcgis_feature',
                preset: 'custom',
                serviceUrl,
                layerLabel:
                  findWeatherLayerByUrl(serviceUrl)?.name ??
                  condition.source?.layerLabel ??
                  '',
              },
            })
          }
        />
      ) : null}

      {isSensor ? (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <Badge variant="outline">Placeholder</Badge>
          <FieldDescription>
            Sensor Alpha is not connected yet. Run now reports placeholder_feed
            until a real connector is wired.
          </FieldDescription>
        </div>
      ) : null}

      {condition.kind === 'attribute' ? (
        attributeConditionsEnabled ? (
          <div className="tw:space-y-2">
            {supportsSchemaFields && serviceUrl && schemaQuery.isLoading ? (
              <Skeleton className="tw:h-9 tw:w-full" />
            ) : null}
            {schemaError ? (
              <p className="tw:m-0 tw:text-xs tw:text-destructive">
                {schemaError.message}
              </p>
            ) : null}

            {supportsSchemaFields &&
            serviceUrl &&
            schemaFields.length > 0 &&
            !schemaQuery.isLoading ? (
              <div className="tw:space-y-2">
                <div className="tw:grid tw:grid-cols-3 tw:gap-2">
                  <Field>
                    <FieldLabel>Field</FieldLabel>
                    {fieldGroups.hasRecommended ? (
                      <Combobox
                        items={
                          fieldItems as { label: string; items: string[] }[]
                        }
                        value={condition.field || null}
                        onValueChange={(next) => {
                          const name = typeof next === 'string' ? next : '';
                          if (name) selectField(name);
                        }}
                        itemToStringValue={(item) => {
                          if (typeof item === 'string') {
                            return fieldLabelByName.get(item) ?? item;
                          }
                          return '';
                        }}
                      >
                        <ComboboxInput
                          placeholder="Search fields…"
                          className="tw:w-full"
                        />
                        <ComboboxContent className="dark">
                          <ComboboxEmpty>No fields found.</ComboboxEmpty>
                          <ComboboxList>
                            {(
                              group: { label: string; items: string[] },
                              index: number
                            ) => (
                              <Fragment key={group.label}>
                                {index > 0 ? <ComboboxSeparator /> : null}
                                <ComboboxGroup items={group.items}>
                                  <ComboboxLabel>{group.label}</ComboboxLabel>
                                  <ComboboxCollection>
                                    {(item: string) => (
                                      <ComboboxItem key={item} value={item}>
                                        <span className="tw:flex tw:flex-col">
                                          <span>
                                            {fieldLabelByName.get(item) ?? item}
                                          </span>
                                          <span className="tw:text-[10px] tw:text-muted-foreground">
                                            {item}
                                          </span>
                                        </span>
                                      </ComboboxItem>
                                    )}
                                  </ComboboxCollection>
                                </ComboboxGroup>
                              </Fragment>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                    ) : (
                      <Combobox
                        items={fieldItems as string[]}
                        value={condition.field || null}
                        onValueChange={(next) => {
                          const name = typeof next === 'string' ? next : '';
                          if (name) selectField(name);
                        }}
                        itemToStringValue={(item) =>
                          fieldLabelByName.get(String(item)) ?? String(item)
                        }
                      >
                        <ComboboxInput
                          placeholder="Search fields…"
                          className="tw:w-full"
                        />
                        <ComboboxContent className="dark">
                          <ComboboxEmpty>No fields found.</ComboboxEmpty>
                          <ComboboxList>
                            {(item) => (
                              <ComboboxItem
                                key={String(item)}
                                value={String(item)}
                              >
                                <span className="tw:flex tw:flex-col">
                                  <span>
                                    {fieldLabelByName.get(String(item)) ??
                                      String(item)}
                                  </span>
                                  <span className="tw:text-[10px] tw:text-muted-foreground">
                                    {String(item)}
                                  </span>
                                </span>
                              </ComboboxItem>
                            )}
                          </ComboboxList>
                        </ComboboxContent>
                      </Combobox>
                    )}
                  </Field>
                  <Field>
                    <FieldLabel>Operator</FieldLabel>
                    {dateFieldDisabled ? (
                      <FieldDescription>
                        Date conditions coming soon.
                      </FieldDescription>
                    ) : (
                      <SimpleSelect
                        value={condition.operator}
                        onChange={(next) =>
                          updateAttribute({
                            operator: next as AttributeCondition['operator'],
                          })
                        }
                        options={operatorOptions}
                      />
                    )}
                  </Field>
                  <Field>
                    <FieldLabel>Value</FieldLabel>
                    {dateFieldDisabled ? (
                      <Input disabled placeholder="Unavailable" />
                    ) : selectedSemantics?.orderedValues?.length ? (
                      <SimpleSelect
                        value={condition.value}
                        onChange={(next) => updateAttribute({ value: next })}
                        options={selectedSemantics.orderedValues.map((c) => ({
                          value: String(c.code),
                          label: c.name,
                        }))}
                        placeholder="Choose intensity"
                      />
                    ) : selectedField?.codedValues?.length ? (
                      <SimpleSelect
                        value={condition.value}
                        onChange={(next) => updateAttribute({ value: next })}
                        options={selectedField.codedValues.map((c) => ({
                          value: String(c.code),
                          label: c.name,
                        }))}
                        placeholder="Choose value"
                      />
                    ) : selectedField?.range ? (
                      <Input
                        type="number"
                        min={selectedField.range.min}
                        max={selectedField.range.max}
                        value={condition.value}
                        onChange={(e) =>
                          updateAttribute({ value: e.target.value })
                        }
                        placeholder={
                          selectedSemantics?.unit
                            ? `${selectedField.range.min}–${selectedField.range.max} ${selectedSemantics.unit}`
                            : `${selectedField.range.min}–${selectedField.range.max}`
                        }
                      />
                    ) : (
                      <Input
                        value={condition.value}
                        onChange={(e) =>
                          updateAttribute({ value: e.target.value })
                        }
                        placeholder={
                          selectedSemantics?.unit
                            ? selectedSemantics.unit
                            : undefined
                        }
                      />
                    )}
                  </Field>
                </div>
                {selectedSemantics?.helpText ? (
                  <FieldDescription>
                    {selectedSemantics.helpText}
                  </FieldDescription>
                ) : null}
              </div>
            ) : !supportsSchemaFields ||
              (!serviceUrl && isCustom) ||
              (serviceUrl &&
                !schemaQuery.isLoading &&
                schemaFields.length === 0) ? (
              <div className="tw:grid tw:grid-cols-3 tw:gap-2">
                <Field>
                  <FieldLabel>Field</FieldLabel>
                  <Input
                    value={condition.field}
                    onChange={(e) =>
                      onChange({ ...condition, field: e.target.value })
                    }
                  />
                </Field>
                <Field>
                  <FieldLabel>Operator</FieldLabel>
                  <SimpleSelect
                    value={condition.operator}
                    onChange={(next) =>
                      onChange({
                        ...condition,
                        operator: next as '>' | '>=' | '<' | '<=' | '==',
                      })
                    }
                    options={[
                      { label: '≥', value: '>=' },
                      { label: '>', value: '>' },
                      { label: '≤', value: '<=' },
                      { label: '<', value: '<' },
                      { label: '=', value: '==' },
                    ]}
                  />
                </Field>
                <Field>
                  <FieldLabel>Value</FieldLabel>
                  <Input
                    value={condition.value}
                    onChange={(e) =>
                      onChange({ ...condition, value: e.target.value })
                    }
                  />
                </Field>
              </div>
            ) : isArcGisLayer && !serviceUrl ? (
              <FieldDescription>
                Choose a layer to configure field, operator, and value.
              </FieldDescription>
            ) : null}

            {supportsSchemaFields &&
            serviceUrl &&
            condition.field &&
            condition.value &&
            !dateFieldDisabled ? (
              <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={previewLoading}
                  onClick={() => void runPreview()}
                >
                  {previewLoading ? (
                    <Loader2 className="tw:h-3.5 tw:w-3.5 tw:animate-spin" />
                  ) : (
                    'Preview matches'
                  )}
                </Button>
                {previewCount != null ? (
                  <span className="tw:text-xs tw:text-muted-foreground">
                    {previewCount} feature{previewCount === 1 ? '' : 's'}{' '}
                    currently match
                  </span>
                ) : null}
                {previewError ? (
                  <span className="tw:text-xs tw:text-destructive">
                    {previewError}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null
      ) : condition.kind === 'aor_intersect' ? (
        <div className="tw:space-y-2">
          <div className="tw:grid tw:grid-cols-2 tw:gap-2">
            <Field>
              <FieldLabel>{aorText('Areas of Responsibility')}</FieldLabel>
              <MultiIdCombobox
                options={aorOptions}
                value={condition.aorIds ?? []}
                onChange={(next) => onChange({ ...condition, aorIds: next })}
                placeholder={aorText('Select AORs…')}
              />
            </Field>
            <Field>
              <FieldLabel>{aorText('Min AORs intersecting')}</FieldLabel>
              <Input
                type="number"
                min={1}
                value={condition.minCount}
                onChange={(e) =>
                  onChange({
                    ...condition,
                    minCount: Math.max(1, Number(e.target.value) || 1),
                  })
                }
              />
            </Field>
          </div>
          {sanitizeProximityKm(condition.proximityKm) > 0 ? (
            <DistanceKmField
              proximityKm={condition.proximityKm}
              onChange={(proximityKm) =>
                onChange({ ...condition, proximityKm })
              }
            />
          ) : null}
          {(condition.aorIds?.length ?? 0) === 0 ? (
            <p className="tw:m-0 tw:text-xs tw:text-destructive">
              {aorText(
                'Select at least one AOR, or this condition will never match.'
              )}
            </p>
          ) : (
            <FieldDescription>
              {aorText(
                sanitizeProximityKm(condition.proximityKm) > 0
                  ? `Fires when at least ${
                      condition.minCount
                    } of the selected AORs are within ${sanitizeProximityKm(
                      condition.proximityKm
                    )} kilometers of the feature.`
                  : `Fires when the hazard geometry overlaps at least ${condition.minCount} of the selected AORs.`
              )}
            </FieldDescription>
          )}
        </div>
      ) : (
        <div className="tw:space-y-2">
          <Field>
            <FieldLabel>Min resources intersecting</FieldLabel>
            <Input
              type="number"
              min={1}
              value={condition.minCount}
              onChange={(e) =>
                onChange({
                  ...condition,
                  minCount: Math.max(1, Number(e.target.value) || 1),
                })
              }
            />
          </Field>
          {sanitizeProximityKm(condition.proximityKm) > 0 ? (
            <DistanceKmField
              proximityKm={condition.proximityKm}
              onChange={(proximityKm) =>
                onChange({ ...condition, proximityKm })
              }
            />
          ) : null}
          <FieldDescription>
            {sanitizeProximityKm(condition.proximityKm) > 0
              ? `Fires when at least ${
                  condition.minCount
                } resources are within ${sanitizeProximityKm(
                  condition.proximityKm
                )} kilometers of the feature.`
              : `Fires when at least ${condition.minCount} resources overlap the feature.`}
          </FieldDescription>
        </div>
      )}
    </div>
  );
}
